import { translate } from '../../i18n/index.js';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { gymsApi } from '../../api/gyms.api.js';
import { bookingIntentsApi } from '../../api/booking-intents.api.js';
import { membershipsApi, type MyMembership } from '../../api/memberships.api.js';
import {
  BackLink,
  formatCurrency,
  formatTime,
  Spinner,
  Tag,
  TierPill,
} from '../../components/design/AtomFitUI.js';
import { SlotGrid } from '../../components/slots/SlotGrid.js';
import { GymInfoDisclosure } from '../../components/gyms/GymInfoDisclosure.js';
import { useSlots } from '../../hooks/useSlots.js';
import { useBookingStore } from '../../store/booking.store.js';
import { useAuthStore } from '../../store/auth.store.js';
import { formatLocalDate } from '../../utils/date.js';
import type { AvailableSlot, Gym } from '@atomfit/shared';

const WALK_IN_MAX_RESERVATION_MINUTES = 90;
const WALK_IN_BOOKING_ADVANCE_DAYS = 31;
const DAYS_PER_PICKER_PAGE = 7;

export function GymDetail() {
  const { gymId } = useParams<{ gymId: string }>();
  const navigate = useNavigate();
  const [dayOffset, setDayOffset] = useState(0);
  const [selectedDurationMinutes, setSelectedDurationMinutes] = useState<number | null>(null);
  const [selected, setSelected] = useState<AvailableSlot[]>([]);
  const [panelSlot, setPanelSlot] = useState<AvailableSlot | null>(null);
  const [isPanelClosing, setIsPanelClosing] = useState(false);
  const [gym, setGym] = useState<Gym | null>(null);
  const [activeMembership, setActiveMembership] = useState<MyMembership | null>(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [gymError, setGymError] = useState<string | null>(null);
  const [bookingIntentError, setBookingIntentError] = useState<string | null>(null);
  const [creatingBookingIntent, setCreatingBookingIntent] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const {
    gymId: storedGymId,
    selectedSlots: storedSelectedSlots,
    setGymId,
    setSelectedSlots,
  } = useBookingStore();
  const { accessToken, account } = useAuthStore();
  const setTenantSlug = useAuthStore((state) => state.setTenantSlug);
  const bookingAdvanceDays =
    activeMembership?.plan.bookingAdvanceDays ?? WALK_IN_BOOKING_ADVANCE_DAYS;

  const days = useMemo(() => {
    const today = new Date();
    return Array.from({ length: bookingAdvanceDays }, (_, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() + index);
      return createPickerDay(date, index);
    });
  }, [bookingAdvanceDays]);

  const selectedPickerDay = days[dayOffset] ?? days[0];
  const selectedPickerDate = new Date(
    `${selectedPickerDay?.value ?? formatLocalDate(new Date())}T12:00:00`,
  );
  const selectedWeekdayIndex = (selectedPickerDate.getDay() + 6) % DAYS_PER_PICKER_PAGE;
  const weekMonday = new Date(selectedPickerDate);
  weekMonday.setDate(selectedPickerDate.getDate() - selectedWeekdayIndex);
  const daysByValue = new Map(days.map((day) => [day.value, day]));
  const visibleDays = Array.from({ length: DAYS_PER_PICKER_PAGE }, (_, index) => {
    const calendarDate = new Date(weekMonday);
    calendarDate.setDate(weekMonday.getDate() + index);
    return daysByValue.get(formatLocalDate(calendarDate)) ?? createPickerDay(calendarDate, null);
  });
  const firstBookingDate = new Date(`${days[0]?.value ?? formatLocalDate(new Date())}T12:00:00`);
  const daysBeforeTodayInFirstWeek = (firstBookingDate.getDay() + 6) % DAYS_PER_PICKER_PAGE;
  const totalWeekCount = Math.ceil(
    (daysBeforeTodayInFirstWeek + days.length) / DAYS_PER_PICKER_PAGE,
  );
  const currentWeekIndex = Math.floor(
    (daysBeforeTodayInFirstWeek + dayOffset) / DAYS_PER_PICKER_PAGE,
  );
  const bookingAdvanceWeeks = Math.max(1, Math.round(bookingAdvanceDays / 7));

  const date = days[dayOffset]?.value ?? formatLocalDate(new Date());
  const { slots, loading, error } = useSlots(gymId ?? null, date);
  const slotDurationMinutes = gym?.slotDurationMinutes ?? 30;
  const maxReservationMinutes =
    activeMembership?.plan.maxReservationMinutes ?? WALK_IN_MAX_RESERVATION_MINUTES;
  const durationOptions = [60, 90, ...(activeMembership?.tier === 'ELITE' ? [120] : [])].filter(
    (minutes) => minutes <= maxReservationMinutes && minutes % slotDurationMinutes === 0,
  );
  const requiredSlotCount = selectedDurationMinutes
    ? selectedDurationMinutes / slotDurationMinutes
    : 0;
  const bookingOptions = useMemo(
    () =>
      gym && requiredSlotCount > 0
        ? buildDurationBookingOptions(slots, requiredSlotCount, gym, nowMs)
        : [],
    [gym, nowMs, requiredSlotCount, slots],
  );
  const selectedBookingOption = bookingOptions.find(
    (option) =>
      selected[0] && String(option.displaySlot.startTime) === String(selected[0].startTime),
  );
  const selectedIsPast = selected.some((slot) => new Date(slot.startTime).getTime() <= nowMs);
  const panelSlotIsPast = panelSlot ? new Date(panelSlot.startTime).getTime() <= nowMs : false;

  useEffect(() => {
    if (!gymId || storedGymId !== gymId || storedSelectedSlots.length === 0) return;

    setSelected(storedSelectedSlots);
    if (gym) {
      setSelectedDurationMinutes(storedSelectedSlots.length * gym.slotDurationMinutes);
    }

    const selectedDate = formatLocalDate(new Date(storedSelectedSlots[0]!.startTime));
    const matchingDay = days.find((day) => day.value === selectedDate);
    if (matchingDay) setDayOffset(matchingDay.index);
  }, [days, gym, gymId, storedGymId, storedSelectedSlots]);

  useEffect(() => {
    if (dayOffset < days.length) return;
    setDayOffset(Math.max(0, days.length - 1));
    setSelected([]);
  }, [dayOffset, days.length]);

  useEffect(() => {
    if (!gymId) return;
    gymsApi
      .get(gymId)
      .then((res) => {
        setGym(res.data);
        if (res.data.tenant?.slug) setTenantSlug(res.data.tenant.slug);
      })
      .catch((err: Error) => setGymError(err.message));
  }, [gymId, setTenantSlug]);

  useEffect(() => {
    if (!accessToken || !account || !gym?.tenantId) {
      setActiveMembership(null);
      setMembershipLoading(false);
      return;
    }

    let isCurrentRequest = true;
    setMembershipLoading(true);
    membershipsApi
      .my()
      .then((response) => {
        if (!isCurrentRequest) return;
        setActiveMembership(
          response.data.data.find((membership) => membership.tenantId === gym.tenantId) ?? null,
        );
      })
      .catch(() => {
        if (isCurrentRequest) setActiveMembership(null);
      })
      .finally(() => {
        if (isCurrentRequest) setMembershipLoading(false);
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [accessToken, account, gym?.tenantId]);

  useEffect(() => {
    if (selected.length > 0) {
      setPanelSlot(selected[selected.length - 1] ?? null);
      setIsPanelClosing(false);
      return;
    }

    if (!panelSlot) return;

    setIsPanelClosing(true);
    const timeout = window.setTimeout(() => {
      setPanelSlot(null);
      setIsPanelClosing(false);
    }, 180);

    return () => window.clearTimeout(timeout);
  }, [panelSlot, selected]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (selectedIsPast) {
      setSelected((current) =>
        current.filter((slot) => new Date(slot.startTime).getTime() > nowMs),
      );
    }
  }, [nowMs, selectedIsPast]);

  useEffect(() => {
    if (selectedDurationMinutes === null || durationOptions.includes(selectedDurationMinutes)) {
      return;
    }
    setSelectedDurationMinutes(null);
    setSelected([]);
  }, [durationOptions, selectedDurationMinutes]);

  useEffect(() => {
    if (loading || selected.length === 0 || requiredSlotCount === 0) return;
    const matchingOption = bookingOptions.find(
      (option) => String(option.slots[0]?.startTime) === String(selected[0]?.startTime),
    );
    if (!matchingOption) {
      setSelected([]);
      return;
    }
    if (!sameSlotSelection(selected, matchingOption.slots)) {
      setSelected(matchingOption.slots);
    }
  }, [bookingOptions, loading, requiredSlotCount, selected]);

  function selectDuration(minutes: number) {
    if (minutes === selectedDurationMinutes) return;
    setSelectedDurationMinutes(minutes);
    setSelected([]);
  }

  function selectStart(displaySlot: AvailableSlot) {
    const option = bookingOptions.find(
      (candidate) => String(candidate.displaySlot.startTime) === String(displaySlot.startTime),
    );
    if (!option) return;
    if (selected[0] && String(selected[0].startTime) === String(displaySlot.startTime)) {
      setSelected([]);
      return;
    }
    setSelected(option.slots);
  }

  function moveDayWindow(direction: -1 | 1) {
    const nextOffset = Math.max(
      0,
      Math.min(days.length - 1, dayOffset + direction * DAYS_PER_PICKER_PAGE),
    );
    if (nextOffset === dayOffset) return;
    setSelected([]);
    setDayOffset(nextOffset);
  }

  useEffect(() => {
    if (!panelSlot) return;

    const viewport = window.visualViewport;
    const updateViewportVars = () => {
      document.documentElement.style.setProperty(
        '--af-viewport-top',
        `${Math.round(window.scrollY + (viewport?.offsetTop ?? 0))}px`,
      );
      document.documentElement.style.setProperty(
        '--af-viewport-height',
        `${Math.round(viewport?.height ?? window.innerHeight)}px`,
      );
    };

    updateViewportVars();
    window.addEventListener('scroll', updateViewportVars, { passive: true });
    window.addEventListener('resize', updateViewportVars);
    viewport?.addEventListener('resize', updateViewportVars);
    viewport?.addEventListener('scroll', updateViewportVars);

    return () => {
      window.removeEventListener('scroll', updateViewportVars);
      window.removeEventListener('resize', updateViewportVars);
      viewport?.removeEventListener('resize', updateViewportVars);
      viewport?.removeEventListener('scroll', updateViewportVars);
      document.documentElement.style.removeProperty('--af-viewport-top');
      document.documentElement.style.removeProperty('--af-viewport-height');
    };
  }, [panelSlot]);

  async function handleBook() {
    if (!gymId || selected.length === 0 || selectedIsPast) return;
    setGymId(gymId);
    setSelectedSlots(selected);
    if (accessToken && account) {
      void navigate('/checkout');
      return;
    }

    setCreatingBookingIntent(true);
    setBookingIntentError(null);
    try {
      const slotStartTimes = selected.map((slot) => new Date(slot.startTime).toISOString());
      const response =
        slotStartTimes.length > 1
          ? await bookingIntentsApi.createBatch(gymId, slotStartTimes)
          : await bookingIntentsApi.create(gymId, slotStartTimes[0]!);
      const redirectToken = response.data.token;
      void navigate(
        `/login?redirect=${encodeURIComponent(`/checkout?bookingIntent=${encodeURIComponent(redirectToken)}`)}`,
      );
      return;
    } catch {
      setBookingIntentError(
        translate('pages.public.gym_detail.handle_book.set_booking_intent_error.error_booking'),
      );
    } finally {
      setCreatingBookingIntent(false);
    }
  }

  return (
    <main className="af-screen af-gym-detail-screen">
      <section
        className={`af-shell af-booking-flow-shell af-gym-detail-shell${panelSlot ? ' has-slot-selection' : ''}`}
      >
        <BackLink to="/gyms">
          {translate('pages.public.gym_detail.gym_detail.back_link.gym_back')}
        </BackLink>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 28,
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div>
            <h1
              style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.5px', margin: '0 0 5px' }}
            >
              {gym?.name ?? translate('pages.public.gym_detail.gym_detail.h1.booking_slot')}
            </h1>
            <div style={{ color: 'var(--af-muted)', fontSize: 13 }}>
              {gym
                ? translate('pages.public.gym_detail.gym_detail.div.copy', {
                    value1: gym.addressLine1,
                    value2: gym.city,
                  })
                : translate('pages.public.gym_detail.gym_detail.div.loading_gym')}
            </div>
          </div>
          <Tag>{translate('pages.public.gym_detail.gym_detail.tag.copy')}</Tag>
        </div>

        {gymError && (
          <div className="af-error" style={{ marginBottom: 18 }}>
            {translate('pages.public.gym_detail.gym_detail.div_error.error_gym')} {gymError}
          </div>
        )}

        {gym && (
          <div className="af-gym-detail-info">
            <GymInfoDisclosure gym={gym} />
          </div>
        )}

        <section className="af-day-picker" aria-labelledby="af-day-picker-title">
          <div className="af-day-picker-header">
            <div>
              <div className="af-day-picker-kicker">
                {translate('pages.public.gym_detail.gym_detail.div_day_picker_kicker.date')}
              </div>
              <h2 id="af-day-picker-title" className="af-day-picker-title">
                {days[dayOffset]?.full ?? ''}
              </h2>
            </div>
            <div className="af-day-picker-navigation">
              <span className="af-day-picker-window">
                {activeMembership
                  ? translate('pages.public.gym_detail.gym_detail.span_day_picker_window.booking', {
                      value1: activeMembership.plan.name,
                      bookingAdvanceWeeks: bookingAdvanceWeeks,
                      value3: formatCzechWeeks(bookingAdvanceWeeks),
                    })
                  : translate(
                      'pages.public.gym_detail.gym_detail.span_day_picker_window.booking_membership',
                      {
                        bookingAdvanceWeeks: bookingAdvanceWeeks,
                        value2: formatCzechWeeks(bookingAdvanceWeeks),
                      },
                    )}
              </span>
              <span className="af-day-picker-pager">
                <button
                  className="af-day-picker-page-button"
                  type="button"
                  onClick={() => moveDayWindow(-1)}
                  disabled={currentWeekIndex === 0}
                  aria-label={translate(
                    'pages.public.gym_detail.gym_detail.button_day_picker_page_button.aria_label_previous',
                  )}
                >
                  {'←'}
                </button>
                <span className="af-day-picker-page-count">
                  {translate('pages.public.gym_detail.gym_detail.span_day_picker_page_count.time')}
                  {currentWeekIndex + 1}{' '}
                  {translate('pages.public.gym_detail.gym_detail.span_day_picker_page_count.copy')}{' '}
                  {totalWeekCount}
                </span>
                <button
                  className="af-day-picker-page-button"
                  type="button"
                  onClick={() => moveDayWindow(1)}
                  disabled={currentWeekIndex === totalWeekCount - 1}
                  aria-label={translate(
                    'pages.public.gym_detail.gym_detail.button_day_picker_page_button.aria_label_next',
                  )}
                >
                  {'→'}
                </button>
              </span>
            </div>
          </div>
          <div
            className="af-day-picker-rail"
            role="tablist"
            aria-label={translate(
              'pages.public.gym_detail.gym_detail.div_day_picker_rail.aria_label_booking_select',
            )}
          >
            {visibleDays.map((day) => {
              const isBookable = day.index !== null;
              const isActive = isBookable && dayOffset === day.index;
              const isToday = day.value === days[0]?.value;
              const isPast = day.value < (days[0]?.value ?? '');
              return (
                <button
                  key={day.value}
                  className={`af-day-option${isActive ? ' is-active' : ''}${isToday ? ' is-today' : ''}${!isBookable ? ' is-outside' : ''}`}
                  onClick={() => {
                    if (!isBookable) return;
                    if (day.index !== dayOffset) setSelected([]);
                    setDayOffset(day.index);
                  }}
                  role="tab"
                  aria-selected={isActive}
                  aria-label={translate(
                    'pages.public.gym_detail.gym_detail.button.aria_label_booking',
                    {
                      day: day.full,
                      today: isToday
                        ? translate('pages.public.gym_detail.gym_detail.button.aria_label_content')
                        : '',
                      outsideBookingWindow: !isBookable
                        ? translate(
                            'pages.public.gym_detail.gym_detail.button.aria_label_content_2',
                          )
                        : '',
                    },
                  )}
                  disabled={!isBookable}
                  type="button"
                >
                  <span className="af-day-option-weekday">{day.short}</span>
                  <span className="af-day-option-date">
                    <strong>{day.num}</strong>
                    <small>{day.month}</small>
                  </span>
                  <span className="af-day-option-state">
                    {isActive
                      ? translate('pages.public.gym_detail.gym_detail.span_day_option_state.copy')
                      : isToday
                        ? translate(
                            'pages.public.gym_detail.gym_detail.span_day_option_state.copy_2',
                          )
                        : !isBookable
                          ? isPast
                            ? '\u00a0'
                            : translate(
                                'pages.public.gym_detail.gym_detail.span_day_option_state.scope',
                              )
                          : '\u00a0'}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="af-duration-picker" aria-labelledby="af-duration-picker-title">
          <div className="af-duration-picker-header">
            <div>
              <div className="af-slot-picker-kicker">
                {translate('pages.public.gym_detail.gym_detail.div_slot_picker_kicker.time')}
              </div>
              <h2 id="af-duration-picker-title" className="af-slot-picker-title">
                {translate('pages.public.gym_detail.gym_detail.h2_slot_picker_title.training')}
              </h2>
            </div>
            <span className="af-duration-picker-limit">
              {membershipLoading
                ? translate(
                    'pages.public.gym_detail.gym_detail.span_duration_picker_limit.membership',
                  )
                : activeMembership
                  ? translate(
                      'pages.public.gym_detail.gym_detail.span_duration_picker_limit.time',
                      {
                        value1: activeMembership.plan.name,
                        maxReservationMinutes: maxReservationMinutes,
                      },
                    )
                  : translate(
                      'pages.public.gym_detail.gym_detail.span_duration_picker_limit.membership_2',
                      {
                        maxReservationMinutes: maxReservationMinutes,
                      },
                    )}
            </span>
          </div>
          <div
            className="af-duration-options"
            role="radiogroup"
            aria-label={translate(
              'pages.public.gym_detail.gym_detail.div_duration_options.aria_label_content',
            )}
          >
            {durationOptions.map((minutes) => {
              const slotCount = minutes / slotDurationMinutes;
              return (
                <button
                  key={minutes}
                  className={`af-duration-option${selectedDurationMinutes === minutes ? ' is-active' : ''}`}
                  type="button"
                  role="radio"
                  aria-checked={selectedDurationMinutes === minutes}
                  onClick={() => selectDuration(minutes)}
                >
                  <span>
                    <strong>{minutes}</strong>
                    <small>
                      {translate('pages.public.gym_detail.gym_detail.small.description')}
                    </small>
                  </span>
                  <em>
                    {slotCount} {formatCzechSlots(slotCount)}
                  </em>
                </button>
              );
            })}
          </div>
        </section>

        {selectedDurationMinutes !== null && (
          <section className="af-slot-picker" aria-labelledby="af-slot-picker-title">
            <div className="af-slot-picker-header">
              <div>
                <div className="af-slot-picker-kicker">
                  {translate('pages.public.gym_detail.gym_detail.div_slot_picker_kicker.copy')}
                </div>
                <h2 id="af-slot-picker-title" className="af-slot-picker-title">
                  {translate('pages.public.gym_detail.gym_detail.h2_slot_picker_title.heading')}
                </h2>
              </div>
              <div
                className="af-slot-picker-rules"
                aria-label={translate(
                  'pages.public.gym_detail.gym_detail.div_slot_picker_rules.aria_label_content',
                )}
              >
                <span className="af-slot-picker-rule is-primary">
                  <strong>
                    {selectedDurationMinutes}{' '}
                    {translate('pages.public.gym_detail.gym_detail.strong.heading')}
                  </strong>
                  <small>{translate('pages.public.gym_detail.gym_detail.small.training')}</small>
                </span>
                <span className="af-slot-picker-rule">
                  <strong>
                    {requiredSlotCount} {'×'} {slotDurationMinutes}{' '}
                    {translate('pages.public.gym_detail.gym_detail.strong.heading_2')}
                  </strong>
                  <small>{translate('pages.public.gym_detail.gym_detail.small.slot')}</small>
                </span>
              </div>
            </div>

            {loading && (
              <div
                className="af-card"
                style={{
                  padding: 28,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  color: 'var(--af-muted)',
                }}
              >
                <Spinner />
                {translate('pages.public.gym_detail.gym_detail.div_card.loading')}
              </div>
            )}
            {error && (
              <div className="af-error">
                {translate('pages.public.gym_detail.gym_detail.div_error.error_slot')} {error}
              </div>
            )}
            {bookingIntentError && (
              <div className="af-error" style={{ marginBottom: 18 }}>
                {bookingIntentError}
              </div>
            )}

            {!loading && !error && (
              <SlotGrid
                slots={bookingOptions.map((option) => option.displaySlot)}
                selected={selectedBookingOption?.displaySlot ?? null}
                onSelect={selectStart}
                durationMinutes={selectedDurationMinutes}
                emptyMessage={translate(
                  'pages.public.gym_detail.gym_detail.slot_grid.empty_message',
                )}
                nowMs={nowMs}
              />
            )}
          </section>
        )}

        {panelSlot &&
          createPortal(
            <SlotSelectionPanel
              creatingBookingIntent={creatingBookingIntent}
              isClosing={isPanelClosing}
              onBook={() => void handleBook()}
              panelSlot={panelSlot}
              panelSlotIsPast={panelSlotIsPast}
              selected={selected}
              slotDurationMinutes={slotDurationMinutes}
            />,

            document.body,
          )}
      </section>
    </main>
  );
}

function SlotSelectionPanel({
  creatingBookingIntent,
  isClosing,
  onBook,
  panelSlot,
  panelSlotIsPast,
  selected,
  slotDurationMinutes,
}: {
  creatingBookingIntent: boolean;
  isClosing: boolean;
  onBook: () => void;
  panelSlot: AvailableSlot;
  panelSlotIsPast: boolean;
  selected: AvailableSlot[];
  slotDurationMinutes: number;
}) {
  const displaySlots = selected.length > 0 ? selected : [panelSlot];
  const firstSlot = displaySlots[0]!;
  const lastSlot = displaySlots[displaySlots.length - 1]!;
  const selectedDates = new Set(displaySlots.map((slot) => formatShortDate(slot.startTime)));
  const spansMultipleDates = selectedDates.size > 1;

  return (
    <>
      <div className="af-slot-selection-gradient-clip" aria-hidden="true">
        <div className="af-slot-selection-gradient" />
      </div>
      <div className={`af-card af-slot-selection${isClosing ? ' is-closing' : ''}`}>
        <div className="af-slot-selection-time">
          <div className="af-slot-selection-label">
            {spansMultipleDates
              ? translate(
                  'pages.public.gym_detail.slot_selection_panel.div_slot_selection_label.slot',
                  {
                    value1: displaySlots.length,
                    value2: selectedDates.size,
                  },
                )
              : translate(
                  'pages.public.gym_detail.slot_selection_panel.div_slot_selection_label.training',
                  {
                    value1: displaySlots.length * slotDurationMinutes,
                    value2: displaySlots.length,
                    value3: formatCzechSlots(displaySlots.length),
                  },
                )}
          </div>
          <div className="af-slot-selection-range">
            {displaySlots.length === 1
              ? translate(
                  'pages.public.gym_detail.slot_selection_panel.div_slot_selection_range.copy',
                  {
                    value1: formatShortDate(panelSlot.startTime),
                    value2: formatTime(panelSlot.startTime),
                    value3: formatTime(panelSlot.endTime),
                  },
                )
              : spansMultipleDates
                ? translate(
                    'pages.public.gym_detail.slot_selection_panel.div_slot_selection_range.copy_2',
                    {
                      value1: formatShortDate(firstSlot.startTime),
                      value2: formatShortDate(lastSlot.startTime),
                    },
                  )
                : translate(
                    'pages.public.gym_detail.slot_selection_panel.div_slot_selection_range.copy_3',
                    {
                      value1: formatShortDate(firstSlot.startTime),
                      value2: formatTime(firstSlot.startTime),
                      value3: formatTime(lastSlot.endTime),
                    },
                  )}
          </div>
        </div>
        {displaySlots.length === 1 && panelSlot.pricingTier !== 'STANDARD' && (
          <TierPill tier={panelSlot.pricingTier} />
        )}
        <div className="af-slot-selection-spacer" />
        <div className="af-slot-selection-price">
          {formatCurrency(
            displaySlots.reduce((total, slot) => total + slot.priceInSmallestUnit, 0),
            panelSlot.currency,
          )}
        </div>
        <button
          className="af-primary-btn af-slot-selection-button"
          onClick={onBook}
          disabled={panelSlotIsPast || creatingBookingIntent}
        >
          {creatingBookingIntent
            ? translate(
                'pages.public.gym_detail.slot_selection_panel.button_primary_btn_slot_selection_button.label',
              )
            : panelSlotIsPast
              ? translate(
                  'pages.public.gym_detail.slot_selection_panel.button_primary_btn_slot_selection_button.slot',
                )
              : translate(
                  'pages.public.gym_detail.slot_selection_panel.button_primary_btn_slot_selection_button.book',
                )}
        </button>
      </div>
    </>
  );
}

function formatShortDate(value: Date | string) {
  return new Date(value).toLocaleDateString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
  });
}

function sortSlotsByStart(first: AvailableSlot, second: AvailableSlot) {
  return new Date(first.startTime).getTime() - new Date(second.startTime).getTime();
}

function areConsecutive(first: AvailableSlot, second: AvailableSlot, gym: Gym) {
  const expectedNextStart =
    new Date(first.startTime).getTime() + (gym.slotDurationMinutes + gym.slotBufferMinutes) * 60000;
  return new Date(second.startTime).getTime() === expectedNextStart;
}

interface DurationBookingOption {
  displaySlot: AvailableSlot;
  slots: AvailableSlot[];
}

function buildDurationBookingOptions(
  availableSlots: AvailableSlot[],
  requiredSlotCount: number,
  gym: Gym,
  nowMs: number,
): DurationBookingOption[] {
  const sortedSlots = [...availableSlots].sort(sortSlotsByStart);
  const options: DurationBookingOption[] = [];

  for (let index = 0; index < sortedSlots.length; index += 1) {
    const block = sortedSlots.slice(index, index + requiredSlotCount);
    if (block.length !== requiredSlotCount) continue;
    if (
      block.some(
        (slot) =>
          slot.status === 'FULL' ||
          slot.status === 'BLOCKED' ||
          new Date(slot.startTime).getTime() <= nowMs,
      )
    ) {
      continue;
    }
    if (block.slice(1).some((slot, blockIndex) => !areConsecutive(block[blockIndex]!, slot, gym))) {
      continue;
    }

    options.push({
      slots: block,
      displaySlot: aggregateBookingBlock(block),
    });
  }

  return options;
}

function aggregateBookingBlock(block: AvailableSlot[]): AvailableSlot {
  const firstSlot = block[0]!;
  const lastSlot = block[block.length - 1]!;
  const publicPriceInSmallestUnit = block.reduce(
    (total, slot) => total + (slot.publicPriceInSmallestUnit ?? slot.priceInSmallestUnit),
    0,
  );
  const priceInSmallestUnit = block.reduce((total, slot) => total + slot.priceInSmallestUnit, 0);
  const pricingTier = block.every((slot) => slot.pricingTier === firstSlot.pricingTier)
    ? firstSlot.pricingTier
    : 'STANDARD';

  return {
    ...firstSlot,
    endTime: lastSlot.endTime,
    priceInSmallestUnit,
    publicPriceInSmallestUnit,
    pricingTier,
    status: block.some((slot) => slot.status === 'LIMITED') ? 'LIMITED' : 'AVAILABLE',
  };
}

function sameSlotSelection(first: AvailableSlot[], second: AvailableSlot[]) {
  return (
    first.length === second.length &&
    first.every(
      (slot, index) =>
        String(slot.startTime) === String(second[index]?.startTime) &&
        slot.status === second[index]?.status &&
        slot.priceInSmallestUnit === second[index]?.priceInSmallestUnit,
    )
  );
}

function createPickerDay<TIndex extends number | null>(date: Date, index: TIndex) {
  return {
    index,
    value: formatLocalDate(date),
    short: date.toLocaleDateString('cs-CZ', { weekday: 'short' }),
    num: date.getDate(),
    month: date.toLocaleDateString('cs-CZ', { month: 'short' }).replace('.', ''),
    full: date.toLocaleDateString('cs-CZ', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }),
  };
}

function formatCzechWeeks(weeks: number) {
  if (weeks === 1) return translate('pages.public.gym_detail.format_czech_weeks.time');
  if (weeks >= 2 && weeks <= 4) return translate('pages.public.gym_detail.format_czech_weeks.copy');
  return translate('pages.public.gym_detail.format_czech_weeks.copy_2');
}

function formatCzechSlots(slots: number) {
  if (slots === 1) return translate('pages.public.gym_detail.format_czech_slots.slot');
  if (slots >= 2 && slots <= 4)
    return translate('pages.public.gym_detail.format_czech_slots.slot_2');
  return translate('pages.public.gym_detail.format_czech_slots.slot_3');
}
